import { useCallback, useState } from "react";
import { AlertCircle, Copy, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/shared/components/form/Button";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { useVerificationRequestsSection } from "../hooks/useVerificationRequestsSection";
import type { VerificationKycLinkStatus } from "../types/auditoria-api.types";
import type { AuditTranslations } from "@/shared/lang/interfaces/audit";
import { formatNumericDateTime } from "../utils/formatNumericDateTime";

const STATUS_STYLES: Record<VerificationKycLinkStatus, string> = {
  ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  USED: "bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-300",
  EXPIRED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const STATUS_TRANSLATION_KEYS: Record<VerificationKycLinkStatus, keyof AuditTranslations> = {
  ACTIVE: "verification_status_active",
  USED: "verification_status_used",
  EXPIRED: "verification_status_expired",
};

export const VerificationRequestsSection = () => {
  const { t } = useTranslations("audit");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const {
    organizationEmail,
    setOrganizationEmail,
    clientEmail,
    setClientEmail,
    verificationLinks,
    isLoading,
    error,
    noAccess,
    hasSearched,
    handleSearch,
    handleReset,
  } = useVerificationRequestsSection();

  const handleCopy = useCallback((url: string, index: number) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }).catch(() => {});
  }, []);

  if (noAccess) {
    return <NoAccessView />;
  }

  const isOrgFieldActive = organizationEmail.trim().length > 0;
  const isClientFieldActive = clientEmail.trim().length > 0;

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t("verification_filters_heading")}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t("verification_label_org_email")}
            </label>
            <input
              type="email"
              value={organizationEmail}
              onChange={(e) => setOrganizationEmail(e.target.value)}
              placeholder={t("verification_placeholder_org_email")}
              disabled={isClientFieldActive}
              className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t("verification_label_client_email")}
            </label>
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder={t("verification_placeholder_client_email")}
              disabled={isOrgFieldActive}
              className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {t("verification_filters_hint")}
        </p>

        <div className="flex gap-3 mt-4">
          <Button
            type="button"
            variant="primary"
            onClick={handleSearch}
            disabled={isLoading || (!organizationEmail.trim() && !clientEmail.trim())}
          >
            {t("filters_button_apply")}
          </Button>
          <Button type="button" variant="outline" onClick={handleReset} disabled={isLoading}>
            {t("filters_button_clear")}
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>
              {error === "NOT_FOUND_ORG"
                ? t("verification_error_org_not_found")
                : error === "NOT_FOUND_CLIENT"
                  ? t("verification_error_client_not_found")
                  : error}
            </span>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-12 flex flex-col items-center justify-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-gray-400 dark:text-gray-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("verification_loading")}
          </p>
        </div>
      )}

      {!isLoading && verificationLinks.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {t("verification_results_title")}
          </h3>
          <div className="space-y-3">
            {verificationLinks.map((link, index) => (
              <div
                key={`${link.type}-${index}`}
                className="flex flex-col gap-2 p-4 bg-gray-50 dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {link.type === "organization"
                      ? t("verification_type_organization")
                      : t("verification_type_client")}
                    {" — "}
                    {link.name}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatNumericDateTime(link.created_at)}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[link.status]}`}
                    >
                      {t(STATUS_TRANSLATION_KEYS[link.status])}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={link.verification_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-green-600 dark:text-green-400 hover:underline break-all flex-1 min-w-0"
                  >
                    <ExternalLink className="h-4 w-4 shrink-0" />
                    <span className="break-all">{link.verification_url}</span>
                  </a>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleCopy(link.verification_url, index)}
                    className="shrink-0"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    {copiedIndex === index ? "✓" : t("verification_btn_copy")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isLoading && !error && hasSearched && verificationLinks.length === 0 && (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("verification_empty")}
          </p>
        </div>
      )}
    </div>
  );
};
